/**
 * Referrals — customer share view + admin list/settings.
 *
 * `ReferralsPage`  → /referrals (customer)
 * `AdminReferrals` → /admin/referrals (super_admin)
 *
 * Uses only the endpoints exposed in referrals.py — no client-side reward
 * logic. Copy button uses the standard clipboard API. Rewards are credited
 * server-side atomically on first-order delivery; this UI only surfaces
 * state.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Gift, Share2, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/features/auth/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type MyReferrals = {
    code: string;
    earned: number;
    history: Array<{
        id: string;
        referred_id: string;
        status: string;
        referrer_reward: number;
        awarded_at: string | null;
        created_at: string;
    }>;
};

export function ReferralsPage() {
    const { data } = useQuery({
        queryKey: ["referrals", "me"],
        queryFn: async () => (await api.get<MyReferrals>("/referrals/me")).data,
    });

    const shareLink = data?.code ? `${window.location.origin}/signup?ref=${data.code}` : "";
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data?.code || "");
            toast.success("Code copied");
        } catch {
            toast.error("Copy failed");
        }
    };
    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({
                    title: "Join TSGo",
                    text: `Use my code ${data?.code} on TSGo — we both get ₹50 on your first order!`,
                    url: shareLink,
                });
            } else {
                await navigator.clipboard.writeText(shareLink);
                toast.success("Link copied");
            }
        } catch {
            /* silent — share dialog was dismissed */
        }
    };

    return (
        <div data-testid="referrals-page" className="mx-auto max-w-2xl px-4 py-8">
            <header className="flex items-center gap-2">
                <Gift className="h-6 w-6 text-brand" />
                <h1 className="text-3xl font-extrabold">Invite friends, earn ₹50</h1>
            </header>
            <p className="mt-2 text-sm text-muted-foreground">
                Share your code — you both get ₹50 in your TSGo Wallet when they place their first delivered order.
            </p>

            <section className="mt-6 rounded-2xl border border-brand/40 bg-brand/5 p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-brand">Your code</p>
                <p className="mt-1 font-mono text-2xl font-extrabold" data-testid="referral-code">
                    {data?.code || "…"}
                </p>
                <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={copy}
                        data-testid="referral-copy"
                        className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-white px-4 py-2 text-xs font-bold hover:bg-muted"
                    >
                        <Copy className="h-3 w-3" /> Copy
                    </button>
                    <button
                        type="button"
                        onClick={share}
                        data-testid="referral-share"
                        className="inline-flex items-center gap-1 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white"
                    >
                        <Share2 className="h-3 w-3" /> Share
                    </button>
                </div>
            </section>

            <section className="mt-6">
                <h3 className="text-lg font-bold">Your referrals</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                    Total earned: ₹{Math.round((data?.earned || 0) / 100)}
                </p>
                <ul className="mt-3 space-y-2 text-sm">
                    {(data?.history || []).map((h) => (
                        <li
                            key={h.id}
                            className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2"
                        >
                            <span className="text-xs font-mono text-muted-foreground">
                                {h.referred_id.slice(0, 8)}…
                            </span>
                            <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${
                                    h.status === "rewarded"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-amber-100 text-amber-700"
                                }`}
                            >
                                {h.status}
                            </span>
                        </li>
                    ))}
                    {(data?.history || []).length === 0 ? (
                        <p className="text-muted-foreground">No referrals yet — share your code.</p>
                    ) : null}
                </ul>
            </section>
        </div>
    );
}

// ---------- Admin ----------

type AdminList = {
    items: Array<{
        id: string;
        referrer_id: string;
        referred_id: string;
        code: string;
        status: string;
        referrer_reward: number;
        referred_reward: number;
        created_at: string;
        awarded_at: string | null;
        wallet_txn_referrer: string | null;
        wallet_txn_referred: string | null;
    }>;
};

type AdminSettings = {
    enabled: boolean;
    referrer_reward: number;
    referred_reward: number;
};

type LeaderRow = {
    rank: number;
    user_id: string;
    email: string;
    full_name: string;
    referral_code: string;
    successful: number;
    invited: number;
    total_earned: number;
};

export function AdminReferrals() {
    const qc = useQueryClient();
    const { data: list } = useQuery({
        queryKey: ["admin", "referrals"],
        queryFn: async () => (await api.get<AdminList>("/admin/referrals")).data,
    });
    const { data: leaderboard } = useQuery({
        queryKey: ["admin", "referrals", "leaderboard"],
        queryFn: async () =>
            (await api.get<{ items: LeaderRow[] }>("/admin/referrals/leaderboard")).data.items,
    });
    const { data: settings } = useQuery({
        queryKey: ["admin", "referrals", "settings"],
        queryFn: async () =>
            (await api.get<AdminSettings>("/admin/referrals/settings")).data,
    });
    const [form, setForm] = useState<AdminSettings | null>(null);
    const current = form || settings;

    const saveMut = useMutation({
        mutationFn: async (payload: AdminSettings) =>
            (await api.put("/admin/referrals/settings", payload)).data,
        onSuccess: () => {
            toast.success("Settings saved");
            qc.invalidateQueries({ queryKey: ["admin", "referrals", "settings"] });
        },
        onError: () => toast.error("Save failed"),
    });

    return (
        <div data-testid="admin-referrals" className="space-y-6">
            <header className="flex items-center gap-2">
                <Users2 className="h-5 w-5 text-brand" />
                <h1 className="text-2xl font-extrabold">Referrals</h1>
            </header>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Settings</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={Boolean(current?.enabled)}
                            onChange={(e) =>
                                setForm({
                                    enabled: e.target.checked,
                                    referrer_reward: current?.referrer_reward ?? 5000,
                                    referred_reward: current?.referred_reward ?? 5000,
                                })
                            }
                            data-testid="referral-enabled"
                        />
                        Referrals enabled
                    </label>
                    <div>
                        <Label>Referrer reward (paise)</Label>
                        <Input
                            type="number"
                            value={current?.referrer_reward ?? 5000}
                            onChange={(e) =>
                                setForm({
                                    enabled: current?.enabled ?? true,
                                    referrer_reward: Number(e.target.value),
                                    referred_reward: current?.referred_reward ?? 5000,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label>Referred reward (paise)</Label>
                        <Input
                            type="number"
                            value={current?.referred_reward ?? 5000}
                            onChange={(e) =>
                                setForm({
                                    enabled: current?.enabled ?? true,
                                    referrer_reward: current?.referrer_reward ?? 5000,
                                    referred_reward: Number(e.target.value),
                                })
                            }
                        />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => current && saveMut.mutate(current)}
                    disabled={saveMut.isPending || !current}
                    className="mt-3 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                    Save
                </button>
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Top 10 referrers</h3>
                <table className="mt-3 w-full text-sm" data-testid="referral-leaderboard">
                    <thead className="text-left text-xs uppercase text-muted-foreground">
                        <tr>
                            <th className="py-2">#</th>
                            <th>Customer</th>
                            <th>Code</th>
                            <th>Invited</th>
                            <th>Successful</th>
                            <th>Earned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(leaderboard || []).map((row) => (
                            <tr key={row.user_id} className="border-t border-border/60">
                                <td className="py-2 font-bold">#{row.rank}</td>
                                <td>{row.full_name || row.email || "—"}</td>
                                <td className="font-mono text-xs">{row.referral_code || "—"}</td>
                                <td>{row.invited}</td>
                                <td>{row.successful}</td>
                                <td>₹{Math.round(row.total_earned / 100)}</td>
                            </tr>
                        ))}
                        {(leaderboard || []).length === 0 ? (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-muted-foreground">
                                    No successful referrals yet.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Log</h3>
                <table className="mt-3 w-full text-sm">
                    <thead className="text-left text-xs uppercase text-muted-foreground">
                        <tr>
                            <th className="py-2">Code</th>
                            <th>Referrer</th>
                            <th>Referred</th>
                            <th>Reward</th>
                            <th>Status</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(list?.items || []).map((r) => (
                            <tr key={r.id} className="border-t border-border/60 font-mono text-xs">
                                <td className="py-2">{r.code}</td>
                                <td>{r.referrer_id.slice(0, 8)}…</td>
                                <td>{r.referred_id.slice(0, 8)}…</td>
                                <td>
                                    ₹{r.referrer_reward / 100} + ₹{r.referred_reward / 100}
                                </td>
                                <td>{r.status}</td>
                                <td>{new Date(r.awarded_at || r.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                        {(list?.items || []).length === 0 ? (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-muted-foreground">
                                    No referrals yet.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
